import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Plus, Upload } from "lucide-react";
import AddEditDialog from "@/components/shared/AddEditDialog";
import UploadDialog from "@/components/shared/UploadDialog";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as ReTooltip, Legend,
} from "recharts";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#151B24] border border-[#2D3748] rounded-sm p-3 text-sm shadow-xl">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="font-data text-xs" style={{ color: p.fill }}>{p.name}: {p.value}%</p>
      ))}
    </div>
  );
};

export default function Benchmarking() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    API.get("/benchmarks")
      .then((res) => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [refreshKey]);

  const benchmarks = data?.benchmarks || [];
  const sectorBench = data?.sector_benchmarks || [];

  const ncreif = benchmarks.filter((b) => b.source === "NCREIF");
  const msci = benchmarks.filter((b) => b.source === "MSCI");
  const greenstreet = benchmarks.filter((b) => b.source === "Green Street");

  if (loading) return <div className="p-6"><Skeleton className="h-96" /></div>;

  const renderComparisonTable = (items) => (
    <table className="w-full">
      <thead><tr className="border-b border-border bg-muted/30">
        <th className="text-left px-4 py-2 text-xs uppercase tracking-wider font-semibold text-muted-foreground">Metric</th>
        <th className="text-right px-4 py-2 text-xs uppercase tracking-wider font-semibold text-muted-foreground">Portfolio</th>
        <th className="text-right px-4 py-2 text-xs uppercase tracking-wider font-semibold text-muted-foreground">Benchmark</th>
        <th className="text-right px-4 py-2 text-xs uppercase tracking-wider font-semibold text-muted-foreground">Spread</th>
      </tr></thead>
      <tbody>
        {items.map((b, i) => (
          <tr key={i} className="border-b border-border hover:bg-muted/50 transition-colors">
            <td className="px-4 py-2.5 text-sm">{b.metric}</td>
            <td className="px-4 py-2.5 text-sm font-data text-right">{b.portfolio_value}%</td>
            <td className="px-4 py-2.5 text-sm font-data text-right">{b.benchmark_value}%</td>
            <td className="px-4 py-2.5 text-sm font-data text-right">
              <span className={b.spread > 0 ? "text-emerald-500" : b.spread < 0 ? "text-red-500" : ""}>
                {b.spread > 0 ? "+" : ""}{b.spread}%
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="p-6 space-y-6" data-testid="benchmarking-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Benchmark Analysis</h1>
          <p className="text-sm text-muted-foreground mt-1">Portfolio performance vs industry benchmarks</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-benchmark-btn" className="gap-1"><Plus size={14} />Add</Button>
          <Button size="sm" variant="secondary" onClick={() => setShowUpload(true)} data-testid="upload-benchmark-btn" className="gap-1"><Upload size={14} />Upload</Button>
        </div>
      </div>

      <Tabs defaultValue="ncreif" data-testid="benchmark-tabs">
        <TabsList className="bg-secondary border border-border">
          <TabsTrigger value="ncreif" data-testid="tab-ncreif">NCREIF</TabsTrigger>
          <TabsTrigger value="msci" data-testid="tab-msci">MSCI</TabsTrigger>
          <TabsTrigger value="greenstreet" data-testid="tab-greenstreet">Green Street</TabsTrigger>
          <TabsTrigger value="sector" data-testid="tab-sector-bench">Sector Comparison</TabsTrigger>
        </TabsList>

        <TabsContent value="ncreif">
          <div className="grid grid-cols-2 gap-4 mt-4">
            <Card className="border border-border bg-card">
              <CardHeader className="border-b border-border p-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold">Portfolio vs NCREIF</CardTitle>
                  <Badge variant="secondary" className="font-data text-xs">2024</Badge>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={ncreif} barGap={4}>
                    <CartesianGrid strokeDasharray="4 4" stroke="#2D3748" vertical={false} />
                    <XAxis dataKey="metric" tick={{ fill: "#9CA3AF", fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#9CA3AF", fontSize: 12 }} axisLine={false} tickLine={false} />
                    <ReTooltip content={<CustomTooltip />} />
                    <Legend formatter={(v) => <span className="text-xs text-muted-foreground">{v}</span>} />
                    <Bar dataKey="portfolio_value" name="Portfolio" fill="#3B82F6" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="benchmark_value" name="NCREIF" fill="#6366F1" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card className="border border-border bg-card">
              <CardHeader className="border-b border-border p-3"><CardTitle className="text-sm font-semibold">NCREIF Comparison Detail</CardTitle></CardHeader>
              <CardContent className="p-0">{renderComparisonTable(ncreif)}</CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="msci">
          <div className="grid grid-cols-2 gap-4 mt-4">
            <Card className="border border-border bg-card">
              <CardHeader className="border-b border-border p-3"><CardTitle className="text-sm font-semibold">Portfolio vs MSCI</CardTitle></CardHeader>
              <CardContent className="p-4">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={msci} barGap={4}>
                    <CartesianGrid strokeDasharray="4 4" stroke="#2D3748" vertical={false} />
                    <XAxis dataKey="metric" tick={{ fill: "#9CA3AF", fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#9CA3AF", fontSize: 12 }} axisLine={false} tickLine={false} />
                    <ReTooltip content={<CustomTooltip />} />
                    <Legend formatter={(v) => <span className="text-xs text-muted-foreground">{v}</span>} />
                    <Bar dataKey="portfolio_value" name="Portfolio" fill="#3B82F6" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="benchmark_value" name="MSCI" fill="#8B5CF6" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card className="border border-border bg-card">
              <CardHeader className="border-b border-border p-3"><CardTitle className="text-sm font-semibold">MSCI Comparison Detail</CardTitle></CardHeader>
              <CardContent className="p-0">{renderComparisonTable(msci)}</CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="greenstreet">
          <div className="grid grid-cols-2 gap-4 mt-4">
            <Card className="border border-border bg-card">
              <CardHeader className="border-b border-border p-3"><CardTitle className="text-sm font-semibold">Portfolio vs Green Street</CardTitle></CardHeader>
              <CardContent className="p-4">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={greenstreet} barGap={4}>
                    <CartesianGrid strokeDasharray="4 4" stroke="#2D3748" vertical={false} />
                    <XAxis dataKey="metric" tick={{ fill: "#9CA3AF", fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#9CA3AF", fontSize: 12 }} axisLine={false} tickLine={false} />
                    <ReTooltip content={<CustomTooltip />} />
                    <Legend formatter={(v) => <span className="text-xs text-muted-foreground">{v}</span>} />
                    <Bar dataKey="portfolio_value" name="Portfolio" fill="#3B82F6" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="benchmark_value" name="Green Street" fill="#10B981" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card className="border border-border bg-card">
              <CardHeader className="border-b border-border p-3"><CardTitle className="text-sm font-semibold">Green Street Detail</CardTitle></CardHeader>
              <CardContent className="p-0">{renderComparisonTable(greenstreet)}</CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="sector">
          <Card className="border border-border bg-card mt-4">
            <CardHeader className="border-b border-border p-3"><CardTitle className="text-sm font-semibold">Sector IRR: Portfolio vs Benchmarks</CardTitle></CardHeader>
            <CardContent className="p-4" data-testid="sector-benchmark-chart">
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={sectorBench} margin={{ bottom: 30 }}>
                  <CartesianGrid strokeDasharray="4 4" stroke="#2D3748" vertical={false} />
                  <XAxis dataKey="sector" tick={{ fill: "#9CA3AF", fontSize: 11 }} angle={-25} textAnchor="end" axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#9CA3AF", fontSize: 12 }} axisLine={false} tickLine={false} domain={[0, 20]} />
                  <ReTooltip content={<CustomTooltip />} />
                  <Legend formatter={(v) => <span className="text-xs text-muted-foreground">{v}</span>} />
                  <Bar dataKey="portfolio_irr" name="Portfolio" fill="#3B82F6" radius={[2, 2, 0, 0]} barSize={20} />
                  <Bar dataKey="ncreif_irr" name="NCREIF" fill="#6366F1" radius={[2, 2, 0, 0]} barSize={20} />
                  <Bar dataKey="msci_irr" name="MSCI" fill="#8B5CF6" radius={[2, 2, 0, 0]} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      <AddEditDialog open={showAdd} entityType="benchmarks" onClose={() => setShowAdd(false)} onSave={refresh} />
      <UploadDialog open={showUpload} entityType="benchmarks" onClose={() => setShowUpload(false)} onSuccess={refresh} />
    </div>
  );
}
